import { Dialog, IconButton } from "@mui/material";
import CloseIcon from "assets/icons/close.svg?react";
import budgetService from "api/budgetService";
import { budgetDialogPaperProps } from "components/Budget/budgetDialog";
import { IBudgetEngagement } from "dto/Budget/IBudgetEngagement";
import { formatMoney } from "utils/formatMoney";
import styles from "./styles.module.scss";

interface Props {
  engagement: IBudgetEngagement;
  onClose: () => void;
}

const formatDate = (iso?: string) => (iso ? iso.slice(0, 10) : "—");

const BudgetEngagementDetailModal = ({ engagement, onClose }: Props) => {
  const { data: expense } = budgetService.useGetExpense(engagement.ExpenseId);
  const { data: paragraphs = [] } = budgetService.useGetParagraphs();

  const quotaLabel = (expenseQuotaId: string) => {
    const eq = expense?.Quotas.find((q) => q.Id === expenseQuotaId);
    if (!eq) return "§?";
    const p = paragraphs.find((x) => x.Id === eq.ParagraphId);
    const suffix = eq.ParagraphSuffix !== "0" ? eq.ParagraphSuffix : "";
    return `§${p?.Symbol ?? "?"}${suffix} - ${p?.Name ?? ""}`;
  };

  return (
    <Dialog
      open
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={budgetDialogPaperProps}
    >
      <div className={styles.wrapper}>
        <div className={styles.header}>
          <span className={styles.title}>Szczegóły zaangażowania</span>
          <IconButton onClick={onClose} size="small" className={styles.close}>
            <CloseIcon className={styles.closeIcon} />
          </IconButton>
        </div>

        <div className={styles.engagementTitle}>{engagement.Title}</div>
        {engagement.Signature && (
          <div className={styles.signature}>{engagement.Signature}</div>
        )}

        <div className={styles.rows}>
          {expense && (
            <div className={styles.row}>
              <span className={styles.label}>Wydatek</span>
              <span className={styles.value}>{expense.Title}</span>
            </div>
          )}
          <div className={styles.row}>
            <span className={styles.label}>Data zaangażowania</span>
            <span className={styles.value}>
              {formatDate(engagement.EngagementDate)}
            </span>
          </div>
          <div className={styles.row}>
            <span className={styles.label}>Termin realizacji</span>
            <span className={styles.value}>
              {formatDate(engagement.ExecutionDeadline)}
            </span>
          </div>
          <div className={styles.row}>
            <span className={styles.label}>Tryb udzielania zamówienia</span>
            <span className={styles.value}>{engagement.GrantingProcedure}</span>
          </div>
          {engagement.GrantingProcedureDescription && (
            <div className={styles.row}>
              <span className={styles.label}>Opis trybu</span>
              <span className={styles.value}>
                {engagement.GrantingProcedureDescription}
              </span>
            </div>
          )}
          {engagement.Justification && (
            <div className={styles.row}>
              <span className={styles.label}>Uzasadnienie</span>
              <span className={styles.value}>{engagement.Justification}</span>
            </div>
          )}
          <div className={styles.row}>
            <span className={styles.label}>Status</span>
            <span className={styles.value}>{engagement.Status ?? "—"}</span>
          </div>
        </div>

        <span className={styles.sectionTitle}>Paragrafy</span>
        <div className={styles.quotas}>
          {engagement.Quotas.map((q) => (
            <div className={styles.quota} key={q.Id}>
              <span className={styles.quotaLabel}>
                {quotaLabel(q.ExpenseQuotaId)}
              </span>
              <span className={styles.quotaAmount}>{formatMoney(q.Amount)}</span>
            </div>
          ))}
        </div>

        <div className={styles.total}>
          <span>Zaangażowane środki</span>
          <strong>{formatMoney(engagement.Amount)}</strong>
        </div>
      </div>
    </Dialog>
  );
};

export default BudgetEngagementDetailModal;
